import { Fragment, useCallback, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileSpreadsheet,
  Loader2,
  RefreshCcw,
} from "lucide-react";
import { useExtractInvoices, useInvoiceLines, useInvoices } from "@/hooks/use-invoices";
import { fetchBlobWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";
import { Button } from "@/components/ui/button";

interface InvoicesSectionProps {
  projectId: string;
}

function eur(n: number | null): string {
  if (n == null) return "—";
  return n.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function InvoicesSection({ projectId }: InvoicesSectionProps) {
  const invoicesQuery = useInvoices(projectId);
  const extract = useExtractInvoices(projectId);

  const data = invoicesQuery.data;
  const items = data?.items ?? [];
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = useCallback((docId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  }, []);

  // Abre el PDF original en una pestaña (renderizado por el navegador con la
  // sesión del usuario). Ventana abierta en el gesto para evitar bloqueo.
  const openOriginal = useCallback(async (documentId: string) => {
    const win = window.open("", "_blank");
    try {
      const blob = await fetchBlobWithAuth(`/api/v1/documents/${documentId}/download`);
      const url = window.URL.createObjectURL(blob);
      if (win) win.location.href = url;
      else window.open(url, "_blank");
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch {
      if (win) win.close();
      showToast.error("No se pudo abrir el documento");
    }
  }, []);

  return (
    <section className="space-y-4">
      {/* Cabecera */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="max-w-2xl">
          <h2 className="text-sm font-heading font-semibold text-ink flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-content-muted" />
            Facturas
          </h2>
          <p className="text-xs text-content-muted mt-1">
            Las facturas se extraen <strong>automáticamente</strong> al procesar
            cada documento. Aquí puedes verlas y verificarlas (totales exactos,
            calculados sobre los datos extraídos).
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => extract.mutate({ force: true })}
          disabled={extract.isPending}
          title="Reprocesa TODOS los documentos (solo si hace falta tras una mejora)"
          className="text-content-muted"
        >
          {extract.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCcw className="w-4 h-4" />
          )}
          Re-extraer
        </Button>
      </div>

      {extract.isPending && (
        <p className="text-xs text-content-muted">
          Leyendo y extrayendo documentos… puede tardar según el número de
          facturas. No cierres la página.
        </p>
      )}

      {extract.data && !extract.isPending && (
        <div className="bg-energia-pale border border-line rounded-md px-3 py-2 text-xs text-ink">
          Última extracción: <strong>{extract.data.facturas}</strong> facturas ·{" "}
          {extract.data.no_facturas} no-facturas · {extract.data.procesados} procesados ·{" "}
          {extract.data.omitidos_sin_texto} sin texto (en ERROR o sin OCR).
        </div>
      )}

      {/* Tabla */}
      {invoicesQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 text-content-muted animate-spin" />
        </div>
      ) : invoicesQuery.isError ? (
        <div className="bg-danger/10 border border-danger/30 rounded-md p-3 text-center text-sm text-danger">
          {invoicesQuery.error?.message ?? "Error cargando las facturas."}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 border-2 border-dashed border-line-strong rounded-md">
          <FileSpreadsheet className="w-8 h-8 text-content-muted/50 mx-auto mb-2" />
          <p className="text-sm text-content font-medium">Sin facturas todavía</p>
          <p className="text-xs text-content-muted mt-0.5">
            Aparecerán aquí automáticamente conforme se procesen los documentos
            del proyecto.
          </p>
        </div>
      ) : (
        <div className="bg-surface rounded-lg border border-line overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-line">
                  <th className="text-left px-4 py-2 text-[11px] font-medium text-content-muted uppercase">Proveedor</th>
                  <th className="text-left px-4 py-2 text-[11px] font-medium text-content-muted uppercase hidden lg:table-cell">NIF</th>
                  <th className="text-left px-4 py-2 text-[11px] font-medium text-content-muted uppercase hidden md:table-cell">Nº</th>
                  <th className="text-left px-4 py-2 text-[11px] font-medium text-content-muted uppercase hidden xl:table-cell">Obra</th>
                  <th className="text-left px-4 py-2 text-[11px] font-medium text-content-muted uppercase hidden lg:table-cell">Fecha</th>
                  <th className="text-right px-4 py-2 text-[11px] font-medium text-content-muted uppercase">Base imponible</th>
                  <th className="text-right px-4 py-2 text-[11px] font-medium text-content-muted uppercase">Total</th>
                  <th className="px-4 py-2 w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line/60">
                {items.map((inv) => (
                  <Fragment key={inv.id}>
                  <tr className="hover:bg-surface-100">
                    <td className="px-4 py-2.5 text-sm text-ink max-w-[28ch] truncate" title={inv.proveedor ?? undefined}>
                      <button
                        onClick={() => toggleExpand(inv.document_id)}
                        className="inline-flex items-center gap-1 text-left hover:text-brand"
                        title="Ver líneas / materiales"
                      >
                        {expanded.has(inv.document_id) ? (
                          <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
                        )}
                        <span className="truncate">{inv.proveedor ?? "—"}</span>
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-content hidden lg:table-cell whitespace-nowrap">{inv.nif ?? "—"}</td>
                    <td className="px-4 py-2.5 text-sm text-content hidden md:table-cell">{inv.numero ?? "—"}</td>
                    <td className="px-4 py-2.5 text-sm text-content hidden xl:table-cell max-w-[20ch] truncate" title={inv.obra ?? undefined}>{inv.obra ?? "—"}</td>
                    <td className="px-4 py-2.5 text-sm text-content hidden lg:table-cell whitespace-nowrap">{inv.fecha ?? "—"}</td>
                    <td className="px-4 py-2.5 text-sm text-content text-right tabular-nums">{eur(inv.base_imponible)}</td>
                    <td className="px-4 py-2.5 text-sm text-content text-right tabular-nums">{eur(inv.total)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => openOriginal(inv.document_id)}
                        className="p-1.5 text-content-muted hover:text-ink rounded transition-colors"
                        title="Ver factura original (PDF)"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                  {expanded.has(inv.document_id) && (
                    <InvoiceLinesRow projectId={projectId} documentId={inv.document_id} />
                  )}
                  </Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-surface-100 border-t border-line font-medium">
                  <td className="px-4 py-2.5 text-sm text-ink" colSpan={5}>
                    Total ({data?.count ?? items.length} facturas)
                  </td>
                  <td className="px-4 py-2.5 text-sm text-ink text-right tabular-nums">
                    {eur(data?.total_base_imponible ?? 0)}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-ink text-right tabular-nums">
                    {eur(data?.total_total ?? 0)}
                  </td>
                  <td className="px-4 py-2.5" />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sub-fila con las líneas/materiales de una factura (carga bajo demanda)
// ---------------------------------------------------------------------------

function InvoiceLinesRow({
  projectId,
  documentId,
}: {
  projectId: string;
  documentId: string;
}) {
  const linesQuery = useInvoiceLines(projectId, documentId, true);
  const lines = linesQuery.data ?? [];

  return (
    <tr className="bg-surface-100/60">
      <td colSpan={8} className="px-8 py-2">
        {linesQuery.isLoading ? (
          <div className="flex items-center gap-2 text-xs text-content-muted py-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando líneas…
          </div>
        ) : lines.length === 0 ? (
          <p className="text-xs text-content-muted py-1">
            Esta factura no tiene líneas de detalle extraídas.
          </p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-[10px] text-content-muted uppercase">
                <th className="text-left py-1 font-medium">Descripción</th>
                <th className="text-right py-1 font-medium w-24">Cantidad</th>
                <th className="text-right py-1 font-medium w-28">P. unitario</th>
                <th className="text-right py-1 font-medium w-28">Importe</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((ln, i) => (
                <tr key={i} className="text-xs text-content">
                  <td className="py-1 pr-2">{ln.descripcion ?? "—"}</td>
                  <td className="py-1 text-right tabular-nums whitespace-nowrap">
                    {ln.cantidad == null ? "—" : `${ln.cantidad} ${ln.unidad ?? ""}`.trim()}
                  </td>
                  <td className="py-1 text-right tabular-nums">
                    {ln.precio_unit == null ? "—" : ln.precio_unit.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
                  </td>
                  <td className="py-1 text-right tabular-nums">
                    {ln.importe == null ? "—" : ln.importe.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </td>
    </tr>
  );
}
